import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Badge, Button, Group, LoadingOverlay, Paper, Stack, Text, Title } from '@mantine/core';
import { useTabStatusContext } from '@/hooks';
import { Icon } from '../Icon';
import styles from './Forms.module.sass';
import type { FormView } from '@/types/form';
import { EditFormModal } from '@/components';
import { useForm, useUpdateFormMutation } from '@/hooks/organization-programs';

const LAYOUT = {
  ARROW_LEFT_ICON_SIZE: 16,
  LOADING_PAPER_MIN_HEIGHT: 200,
};

const Forms = () => {
  const { id: churchId } = useParams<{ id: string }>();
  const { refreshTabStatus } = useTabStatusContext();
  const { data: formData, isLoading, isError } = useForm(churchId);
  const updateFormMutation = useUpdateFormMutation(churchId ?? '');
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);

  if (!churchId) {
    return (
      <Paper p="xl" radius="md" w="100%">
        <Text c="red">Church ID not found</Text>
      </Paper>
    );
  }

  const handleEditFormPageData = () => {
    setIsEditModalOpen(true);
  };

  const handleCloseEditFormData = () => {
    setIsEditModalOpen(false);
  };

  const handleSaveFormData = async (updatedData: FormView) => {
    await updateFormMutation.mutateAsync({
      isActive: updatedData.isActive,
    });
    setIsEditModalOpen(false);
    refreshTabStatus();
  };

  if (isLoading) {
    return (
      <Paper p="xl" radius="md" w="100%" pos="relative" style={{ minHeight: LAYOUT.LOADING_PAPER_MIN_HEIGHT }}>
        <LoadingOverlay visible />
      </Paper>
    );
  }

  if (isError || !formData) {
    return (
      <Paper p="xl" radius="md" w="100%">
        <Text c="red">Failed to load form page data</Text>
      </Paper>
    );
  }

  return (
    <>
      <Paper p="xl" radius="md" w="100%">
        <Stack gap="lg">
          <Group justify="space-between" align="center">
            <Title order={3}>Form</Title>
            <Button
              onClick={handleEditFormPageData}
              variant="default"
              size="xs"
              leftSection={<Icon name="IconEdit" size={LAYOUT.ARROW_LEFT_ICON_SIZE} />}
            >
              Edit
            </Button>
          </Group>

          <Group gap="md" align="center">
            <Group gap="md" align="center" flex="1">
              <Text className={styles.label} size="sm">
                Status:
              </Text>
              <Badge color={formData?.isActive ? 'green' : 'red'} variant="light">
                {formData?.isActive ? 'Active' : 'Inactive'}
              </Badge>
            </Group>
          </Group>
        </Stack>
      </Paper>

      <EditFormModal
        opened={isEditModalOpen}
        formData={formData}
        onClose={handleCloseEditFormData}
        onSave={handleSaveFormData}
      />
    </>
  );
};

export default Forms;
